import React, { useState, useMemo, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Offcanvas,
  Row,
} from "react-bootstrap";
import Breadcrumb from "Common/BreadCrumb";
import TableContainer from "Common/TableContainer";
import { Link } from "react-router-dom";
import {
  getAllPinCodes,
  addPinCode,
  updatePinCode,
  deletePinCode,
} from "api/apis";

interface PinCode {
  _id: string;
  pincode: string;
}

const PinCode = () => {
  document.title = "PinCode";

  const [showPinCodeDetails, setShowPinCodeDetails] = useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<PinCode | null>(null);
  const [pinCodes, setPinCodes] = useState<PinCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formErrors, setFormErrors] = useState<any>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [formData, setFormData] = useState({
    pincode: "",
  });

  useEffect(() => {
    const fetchPinCodes = async () => {
      try {
        setLoading(true);
        const data = await getAllPinCodes();
        setPinCodes(data);
      } catch (error) {
        console.error("Failed to fetch pincodes", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPinCodes();
  }, []);

  const handleDeletePinCode = async () => {
    if (!selectedId) return;

    try {
      setLoading(true);
      await deletePinCode(selectedId);

      // Refresh pincodes list
      const updatedPinCodes = await getAllPinCodes();
      setPinCodes(updatedPinCodes);

      // Close modal and reset
      setDeleteModal(false);
      setSelectedId(null);
    } catch (error) {
      console.error("Failed to delete pincode:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev: any) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors: any = {};

    if (!formData.pincode.trim()) {
      errors.pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      errors.pincode = "Pincode must be 6 digits";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!validateForm()) {
    return;
  }

  setLoading(true);

  try {
    if (isEditMode && editingId) {
      // Update existing pincode
      await updatePinCode(editingId, { pincode: formData.pincode.trim() });
    } else {
      // Create new pincode
      await addPinCode({ pincode: formData.pincode.trim() });
    }

    // Reset form and close modal
    resetForm();
    setModalAddPinCode(false);

    // Refresh pincodes list
    const updatedPinCodes = await getAllPinCodes();
    setPinCodes(updatedPinCodes);
  } catch (error) {
    console.error(
      `Failed to ${isEditMode ? "update" : "create"} pincode:`,
      error
    );
  } finally {
    setLoading(false);
  }
};

  const resetForm = () => {
    setFormData({
      pincode: "",
    });
    setFormErrors({});
    setIsEditMode(false);
    setEditingId(null);
  };

  const handleEditPinCode = (pincode: PinCode) => {
    setIsEditMode(true);
    setEditingId(pincode._id);
    setFormData({
      pincode: pincode.pincode,
    });
    setModalAddPinCode(true);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Pincode",
        accessor: "pincode",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Action",
        disableFilters: true,
        filterable: true,
        accessor: (cellProps: PinCode) => {
          return (
            <ul className="hstack gap-2 list-unstyled mb-0">
              <li>
                <Link
                  to="#pincodeDetails"
                  data-bs-toggle="offcanvas"
                  className="badge bg-dark-subtle text-body view-item-btn"
                  onClick={() => {
                    setShowDetails(cellProps);
                    setShowPinCodeDetails(!showPinCodeDetails);
                  }}
                >
                  View
                </Link>
              </li>
              <li>
                <Button
                  variant="link"
                  className="badge bg-primary-subtle text-primary edit-item-btn p-0 border-0"
                  onClick={() => handleEditPinCode(cellProps)}
                >
                  Edit
                </Button>
              </li>
              <li>
                <Button
                  variant="link"
                  className="badge bg-danger-subtle text-danger remove-item-btn p-0 border-0"
                  onClick={() => {
                    setSelectedId(cellProps._id);
                    setDeleteModal(true);
                  }}
                >
                  Delete
                </Button>
              </li>
            </ul>
          );
        },
      },
    ],
    [showPinCodeDetails]
  );

  const [modalAddPinCode, setModalAddPinCode] = useState<boolean>(false);

  const toggleAddPinCodeModal = () => {
    if (modalAddPinCode) {
      resetForm();
    }
    setModalAddPinCode(!modalAddPinCode);
  };

  const filteredPinCodes = useMemo(() => {
    if (!searchTerm) return pinCodes;
    return pinCodes.filter((pincode) => {
      return pincode.pincode.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [pinCodes, searchTerm]);

  const paginatedPinCodes = useMemo(() => {
    const startIndex = currentPage * pageSize;
    return filteredPinCodes.slice(startIndex, startIndex + pageSize);
  }, [filteredPinCodes, currentPage, pageSize]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumb title="PinCode" pageTitle="More" />
          <div id="pinCodesList">
            <Row>
              <Col xxl={12}>
                <Card>
                  <Card.Body>
                    <Row className="align-items-center">
                      <Col xxl={3} md={5}>
                        <div className="search-box mb-3 mb-md-0">
                          <input
                            type="text"
                            className="form-control search"
                            id="searchPinCodeList"
                            placeholder="Search by pincode..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>
                      </Col>
                      <Col className="col-md-auto ms-auto">
                        <Button
                          variant="success"
                          onClick={toggleAddPinCodeModal}
                          className="add-btn"
                        >
                          <i className="bi bi-plus-circle me-1 align-middle"></i>
                          Add Pincode
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col lg={12}>
                <Card>
                  <Card.Body className="p-0">
                    <TableContainer
                      columns={columns || []}
                      data={paginatedPinCodes}
                      iscustomPageSize={true}
                      isBordered={false}
                      customPageSize={pageSize}
                      className="custom-header-css table align-middle table-nowrap"
                      tableClass="table-centered align-middle table-nowrap mb-0"
                      theadClass="text-muted table-light"
                      SearchPlaceholder="Search Pincodes..."
                      manualPagination={true}
                      pageCount={Math.ceil(filteredPinCodes.length / pageSize)}
                      currentPage={currentPage}
                      onPageChange={setCurrentPage}
                      onPageSizeChange={setPageSize}
                    />
                    <div className="noresult" style={{ display: "none" }}>
                      <div className="text-center">
                        <h5 className="mt-2">Sorry! No Result Found</h5>
                        <p className="text-muted mb-0">
                          We've searched more than 150+ pincodes but didn't find
                          any matches.
                        </p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>

          <Modal
            id="showModal"
            className="fade zoomIn"
            size="lg"
            show={modalAddPinCode}
            onHide={toggleAddPinCodeModal}
            centered
          >
            <Modal.Header className="px-4 pt-4" closeButton>
              <h5 className="modal-title fs-18" id="exampleModalLabel">
                {isEditMode ? "Edit Pincode" : "Add Pincode"}
              </h5>
            </Modal.Header>
            <Modal.Body className="p-4">
              <Form className="tablelist-form" onSubmit={handleSubmit}>
                <Row>
                  <Col lg={12}>
                    <div className="mb-3">
                      <Form.Label htmlFor="pincode">
                        Pincode <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        id="pincode"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleInputChange}
                        placeholder="Enter 6-digit pincode"
                        isInvalid={!!formErrors.pincode}
                        maxLength={6}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.pincode}
                      </Form.Control.Feedback>
                    </div>
                  </Col>
                  <Col lg={12} className="modal-footer">
                    <div className="hstack gap-2 justify-content-end">
                      <Button
                        variant="light"
                        onClick={toggleAddPinCodeModal}
                        disabled={loading}
                      >
                        <i className="ri-close-line align-bottom me-1"></i>
                        Close
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            {isEditMode ? "Updating..." : "Creating..."}
                          </>
                        ) : (
                          <>
                            <i
                              className={`ri-${
                                isEditMode ? "edit" : "save"
                              }-line align-bottom me-1`}
                            ></i>
                            {isEditMode ? "Update Pincode" : "Add Pincode"}
                          </>
                        )}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Modal.Body>
          </Modal>
        </Container>
      </div>

      <Offcanvas
        show={showPinCodeDetails}
        onHide={() => setShowPinCodeDetails(!showPinCodeDetails)}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {showDetails?.pincode || "Pincode Details"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mt-3">
            <div className="table-responsive">
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td>
                      <span className="text-muted">Pincode</span>
                    </td>
                    <td>
                      <span className="fw-medium">
                        {showDetails?.pincode}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Delete Modal */}
      <Modal show={deleteModal} onHide={() => setDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Delete Pincode</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete this pincode? This action cannot be
            undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeletePinCode}
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default PinCode;